import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, ChevronRight, PlayCircle } from "lucide-react";
import { useDemoMode } from "../context/DemoModeContext";
import { useAuth } from "../context/AuthContext";
import { track } from "../lib/analytics";

/**
 * DemoScript — scripted tour overlay used during investor / press
 * demos. Mounts on the Routine page; only renders when demo mode is
 * active AND the user is admin AND they've pressed "Start scripted
 * demo" on the DemoControlsCard.
 *
 * Each beat fires a narrated voiceover via the passed-in `tts.speak`
 * helper and auto-advances ~6.5s after speech start. Founder can tap
 * Next to advance early or × to exit the tour.
 *
 * Beats are deliberately tied to features the founder wants to land
 * with investors:
 *   1. Affirming intro — "create the face you see inside"
 *   2. Skin signals — "we read undertone from your selfie"
 *   3. Face-shape contour — "AR mirror tunes contour to your shape"
 *   4. Voice commands — "you don't have to touch the screen"
 *   5. Share moment — "every finished look credits TruBeauté"
 *
 * The script is plain data so future iterations / seasonal pitches
 * can swap in new narration without touching the overlay logic.
 */
const BEATS = [
  {
    id: "welcome",
    title: "Welcome to TruBeauté",
    body:
      "Your digital drag mom — helping you create the face you see inside.",
    spoken:
      "Welcome to TruBeauté. Your digital drag mom — helping you create the face you see inside. Let me show you around.",
  },
  {
    id: "skin_signals",
    title: "We read your skin signals",
    body:
      "From your selfie, we detect undertone, depth, and texture — then match products that actually flatter you.",
    spoken:
      "From your selfie, we detect undertone, depth, and texture — then match products that actually flatter you.",
  },
  {
    id: "face_shape",
    title: "Contour tuned to your face shape",
    body:
      "Our AR mirror classifies your shape — heart, oval, long, round, square, diamond — and adapts every contour stroke.",
    spoken:
      "Our AR mirror classifies your face shape and adapts every contour stroke. Heart shape gets temple softening. Long shape gets horizontal contour to shorten. Diamond gets a short stroke on the cheek peak. Every face, gracefully.",
  },
  {
    id: "voice",
    title: "Hands-free with Trouvé",
    body:
      "Just say 'Hey Trouvé, next step' — your personal drag mom guides you through every motion, no tapping required.",
    spoken:
      "Just say: Hey Trouvé, next step. I'm your personal drag mom — and I'll guide you through every motion, hands-free.",
  },
  {
    id: "share",
    title: "Share the look you created",
    body:
      "Every finished routine ends with a beautiful Instagram-Story share card — face card never declines with TruBeauté.",
    spoken:
      "Every finished routine ends with a beautiful share card. Your look, your celebration — face card never declines with TruBeauté.",
  },
];

const BEAT_DURATION_MS = 7000;

const DemoScript = ({ tts }) => {
  const { active: demoActive } = useDemoMode();
  const { user } = useAuth();
  const [running, setRunning] = useState(false);
  const [beatIdx, setBeatIdx] = useState(0);
  const timerRef = useRef(null);

  const currentBeat = BEATS[beatIdx];

  const stop = useCallback(() => {
    setRunning(false);
    setBeatIdx(0);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    tts?.stop?.();
    track("demo_script_stopped");
  }, [tts]);

  const advance = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    if (beatIdx + 1 >= BEATS.length) {
      track("demo_script_completed");
      stop();
      return;
    }
    setBeatIdx((idx) => idx + 1);
    track("demo_script_advanced", { beat: BEATS[beatIdx + 1]?.id });
  }, [beatIdx, stop]);

  // Speak the current beat + auto-advance after a fixed delay.
  // Speech may run longer than the timer on slow connections; we still
  // auto-advance because demos benefit from steady pacing.
  useEffect(() => {
    if (!running || !currentBeat) return;
    tts?.speak?.(currentBeat.spoken);
    timerRef.current = setTimeout(() => advance(), BEAT_DURATION_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [running, beatIdx, currentBeat, tts, advance]);

  const start = useCallback(() => {
    setBeatIdx(0);
    setRunning(true);
    track("demo_script_started");
  }, []);

  // Listen for an "open the demo script" event so the
  // DemoControlsCard (a totally separate subtree) can launch the tour
  // without us having to thread props through 5 layers of routing.
  useEffect(() => {
    const onLaunch = () => start();
    window.addEventListener("trubeaute:demo-script-start", onLaunch);
    return () =>
      window.removeEventListener("trubeaute:demo-script-start", onLaunch);
  }, [start]);

  // Stop if demo mode is turned off mid-tour
  useEffect(() => {
    if (!demoActive && running) stop();
  }, [demoActive, running, stop]);

  // Don't render when not running
  if (!user?.is_admin || !demoActive || !running || !currentBeat) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="demo-script-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[90] pointer-events-none flex items-end sm:items-center justify-center px-4 pb-24 sm:pb-0"
        data-testid="demo-script-overlay"
      >
        {/* Subtle radial darken so the spotlight reads but the underlying
            UI is still visible. */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#1a1416]/40 to-[#1a1416]/70" />

        <motion.div
          key={currentBeat.id}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 30 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="relative max-w-md w-full bg-gradient-to-br from-[#1F1418] via-[#2C2123] to-[#5D4050] border border-[#D5A8A4]/40 rounded-3xl p-6 shadow-2xl text-[#FAF6F2] pointer-events-auto"
          data-testid={`demo-script-beat-${currentBeat.id}`}
        >
          <button
            type="button"
            onClick={stop}
            aria-label="Exit scripted demo"
            className="absolute top-3 right-3 h-8 w-8 rounded-full text-white/65 hover:text-white hover:bg-white/10 transition-colors flex items-center justify-center"
            data-testid="demo-script-close"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="flex items-start gap-3 mb-3">
            <div className="shrink-0 h-10 w-10 rounded-full bg-[#FFE6D7]/15 border border-[#FFE6D7]/40 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-[#FFE6D7]" />
            </div>
            <div className="flex-1 pt-0.5">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#D5A8A4] mb-0.5">
                Trouvé · Beat {beatIdx + 1}/{BEATS.length}
              </div>
              <h3
                className="text-xl font-medium leading-tight"
                data-testid="demo-script-title"
              >
                {currentBeat.title}
              </h3>
            </div>
          </div>

          <p
            className="text-sm text-white/80 leading-relaxed mb-5"
            data-testid="demo-script-body"
          >
            {currentBeat.body}
          </p>

          {/* Progress dots */}
          <div className="flex items-center gap-1.5 mb-4">
            {BEATS.map((b, i) => (
              <span
                key={b.id}
                className={`h-1 rounded-full transition-all ${
                  i === beatIdx
                    ? "w-8 bg-[#FFE6D7]"
                    : i < beatIdx
                    ? "w-4 bg-[#D5A8A4]/60"
                    : "w-4 bg-white/15"
                }`}
              />
            ))}
          </div>

          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={stop}
              className="text-xs text-white/60 hover:text-white transition-colors px-2 py-1"
              data-testid="demo-script-stop"
            >
              End tour
            </button>
            <button
              type="button"
              onClick={advance}
              className="inline-flex items-center gap-1.5 bg-[#FFE6D7] hover:bg-white text-[#5D4050] rounded-full px-4 py-2 text-sm font-medium transition-colors"
              data-testid="demo-script-next"
            >
              {beatIdx + 1 >= BEATS.length ? "Finish" : "Next"}
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * Helper used by DemoControlsCard to launch the script. Decoupled via
 * a window event so the card and the overlay can live in different
 * routes without prop-drilling.
 */
export const launchDemoScript = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("trubeaute:demo-script-start"));
  }
};

export default DemoScript;
